import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import { API_CONFIG } from '../config/api.config';
import { ApiError, ApiResponse } from '../types';
import { safeLocalStorage } from '../utils/safeStorage';

type NormalizedApiError = ApiError & { status?: number; refreshConflict?: boolean };

const REFRESH_CONFLICT_RETRY_MS = 1000;
// After a transient refresh failure, 401s fail fast for this long instead of each starting a refresh.
const REFRESH_FAILURE_COOLDOWN_MS = 5000;
// Backend refusals of the token itself (mostly sent as HTTP 400): only these, or a 401, may end the session.
// A 403 without one of these codes (edge/WAF page, bare body) is treated as transient.
const REFRESH_REJECTED_CODES = new Set([
  'INVALID_REFRESH_TOKEN', 'REFRESH_TOKEN_EXPIRED', 'ACCOUNT_SUSPENDED', 'MISSING_TOKEN',
  'SESSION_COMPROMISED', 'ACCOUNT_DISABLED',
]);

const isRefreshRejected = (error: unknown): boolean => {
  const { status, code } = (error ?? {}) as Partial<NormalizedApiError>;
  return status === 401 || (code !== undefined && REFRESH_REJECTED_CODES.has(code));
};

let inFlight = 0;
/** Number of API requests currently in flight (used by the "page rendered" signal, see RouteStateConsumer). */
export const getApiInFlight = (): number => inFlight;

class ApiService {
  private api: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;
  private refreshFailure: { error: unknown; until: number } | null = null;

  constructor() {
    // Support E2E environment override
    const baseURL = import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL;

    this.api = axios.create({
      baseURL,
      timeout: API_CONFIG.TIMEOUT,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // In-flight counter. Registered first so the response side runs before the refresh/error handling below.
    this.api.interceptors.request.use((config: InternalAxiosRequestConfig) => { inFlight += 1; return config; });
    this.api.interceptors.response.use(
      (response) => { inFlight = Math.max(0, inFlight - 1); return response; },
      (error) => { inFlight = Math.max(0, inFlight - 1); return Promise.reject(error); },
    );

    // Strip leading slash to prevent Axios from replacing baseURL path
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (config.url) {
          config.url = config.url.replace(/^\/+/, ''); // Remove leading slash
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Authorization interceptor (skip auth routes)
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getToken();

        // Don't add token for auth routes
        const url = config.url || '';
        const isAuthRoute = /^auth\/(login|register|refresh|logout|google(?:\/|$))/.test(url);

        if (token && config.headers && !isAuthRoute) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Token expired, try to refresh (skip for auth routes)
        const url = originalRequest.url || '';
        const isAuthRoute = /^auth\/(login|register|refresh|logout|google(?:\/|$))/.test(url);

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthRoute) {
          originalRequest._retry = true;
          const authorization = originalRequest.headers?.Authorization;
          const sentToken = typeof authorization === 'string' ? authorization.replace(/^Bearer /, '') : null;

          try {
            const newToken = await this.refreshToken(sentToken);
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return this.api(originalRequest);
          } catch (refreshError) {
            // Logout only on a definitive refusal: an API outage must not end a valid session.
            if (isRefreshRejected(refreshError)) {
              this.logout();
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private getToken(): string | null {
    return safeLocalStorage.getItem('token');
  }

  private setToken(token: string): void {
    safeLocalStorage.setItem('token', token);
  }

  private postRefresh() {
    return this.api.post<ApiResponse<{ token: string }>>(API_CONFIG.ENDPOINTS.AUTH.REFRESH);
  }

  /** Token stored by another tab since `sentToken` was sent, if any: replaying with it avoids a second rotation. */
  private fresherToken(sentToken: string | null): string | null {
    const stored = this.getToken();
    return stored && stored !== sentToken ? stored : null;
  }

  private async refreshToken(sentToken: string | null = null): Promise<string> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }
    // A refresh already completed since this request was sent (this tab or another): replay with that token.
    const fresher = sentToken ? this.fresherToken(sentToken) : null;
    if (fresher) return fresher;
    if (this.refreshFailure && Date.now() < this.refreshFailure.until) {
      // Only reached with a null sentToken here: any stored token is newer than the request.
      const stored = this.fresherToken(sentToken);
      return stored ? stored : Promise.reject(this.refreshFailure.error);
    }

    this.refreshTokenPromise = (async () => {
      try {
        // Runs inside the single-flight promise, so requests waiting on this refresh share the retry too.
        const response = await this.postRefresh().catch(async (error: NormalizedApiError) => {
          if (!error.refreshConflict) throw error;
          await new Promise((resolve) => setTimeout(resolve, REFRESH_CONFLICT_RETRY_MS));
          // Signed out during the wait: do not bring the session back.
          if (!this.getToken()) throw error;
          // Another tab refreshed during the wait: use its token instead of rotating again.
          return this.fresherToken(sentToken) ?? this.postRefresh();
        });
        if (typeof response === 'string') return response;

        const { token } = response.data.data;
        this.setToken(token);

        return token;
      } catch (error) {
        if (!isRefreshRejected(error)) {
          this.refreshFailure = { error, until: Date.now() + REFRESH_FAILURE_COOLDOWN_MS };
        }
        throw error;
      } finally {
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  private logout(): void {
    this.refreshFailure = null;
    const hadToken = safeLocalStorage.getItem('token');
    safeLocalStorage.removeItem('token');
    safeLocalStorage.removeItem('refreshToken');
    safeLocalStorage.removeItem('user');

    if (hadToken) {
      globalThis.dispatchEvent(new Event('auth:logout'));
    }
  }

  private handleError(error: AxiosError<ApiError>): NormalizedApiError {
    // Handle 502/503 gateway errors that return HTML instead of JSON
    if (error.response?.status === 502 || error.response?.status === 503) {
      return {
        code: 'SERVICE_UNAVAILABLE',
        message: 'Le service est temporairement indisponible. Veuillez réessayer.',
        timestamp: new Date().toISOString(),
        status: error.response.status,
        ...(error.response.data?.code === 'REFRESH_CONFLICT' && { refreshConflict: true }),
      };
    }

    // Check if response data is a valid JSON object (not HTML string)
    if (error.response?.data && typeof error.response.data === 'object') {
      return {
        ...error.response.data,
        status: error.response.status,
      };
    }

    // Handle HTML error responses or other non-JSON data
    if (error.response?.data && typeof error.response.data === 'string') {
      return {
        code: 'RUNTIME_ERROR',
        message: 'Une erreur serveur s\'est produite. Veuillez réessayer.',
        timestamp: new Date().toISOString(),
        status: error.response.status,
      };
    }

    return {
      code: 'NETWORK_ERROR',
      message: error.message || 'An unexpected error occurred',
      timestamp: new Date().toISOString(),
      status: error.response?.status,
    };
  }

  // Public methods - strip leading slash to prevent baseURL path loss
  async get<T>(url: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    const response = await this.api.get<ApiResponse<T>>(url.replace(/^\/+/, ''), { params });
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.post<ApiResponse<T>>(url.replace(/^\/+/, ''), data, config);
    return response.data;
  }

  async postRaw<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url.replace(/^\/+/, ''), data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.put<ApiResponse<T>>(url.replace(/^\/+/, ''), data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.patch<ApiResponse<T>>(url.replace(/^\/+/, ''), data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.delete<ApiResponse<T>>(url.replace(/^\/+/, ''), config);
    return response.data;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for API compatibility
  saveAuthData(token: string, _refreshToken?: string): void {
    this.setToken(token);
    this.refreshFailure = null;
    safeLocalStorage.removeItem('refreshToken');
  }

  clearAuthData(): void {
    this.logout();
  }
}

export const apiService = new ApiService();
